import { useCallback, useEffect, useMemo, useState } from 'react';

import { supabase } from '@/lib/customSupabaseClient';
import {
  buildMonthCells,
  buildWeekdayLabels,
  dateKey,
  defaultDateForMonth,
  mapCompletedRow,
  monthRange,
  monthWindow,
  msUntilNextMidnight,
  toISODate,
} from '@/lib/athleteCalendar';
import {
  buildProgramScheduleMap,
  firstProjectedDate,
  formatStartDateLabel,
  hasFutureSessions,
} from '@/lib/programSchedule';

function bucketByDate(rows, getKey) {
  const map = new Map();
  rows.forEach((row) => {
    const key = getKey(row);
    if (!key) return;
    map.set(key, [...(map.get(key) ?? []), row]);
  });
  return map;
}

async function fetchProgramSchedule(userId) {
  const empty = { activations: [], sessionsById: {} };
  const { data: userPrograms, error: programsError } = await supabase.rpc(
    'staff_get_user_programs',
    { p_user_id: userId, p_active_only: true }
  );

  if (programsError) throw programsError;
  const rows = userPrograms ?? [];
  if (rows.length === 0) return empty;

  const programIds = rows.map((row) => row.program_id);
  const { data: sessions, error: sessionsError } = await supabase.rpc(
    'get_user_programs_sessions_batch',
    {
      p_user_id: userId,
      p_program_ids: programIds,
    }
  );
  if (sessionsError) throw sessionsError;

  const programNames = new Map(
    rows.map((row) => [row.program_id, row.program_name || ''])
  );

  const sessionsById = {};
  (sessions ?? []).forEach((session) => {
    sessionsById[session.id] = {
      id: session.id,
      name: session.name,
      programId: session.program_id,
      programName: programNames.get(session.program_id) ?? '',
      weekNumber: session.week_number ?? null,
      dayOfWeek: session.day_of_week ?? null,
      sessionNumber: session.session_number ?? null,
      durationMinutes: session.duration_minutes ?? null,
    };
  });

  return {
    activations: rows.map((row) => ({
      programId: row.program_id,
      activatedAt: row.activated_at,
      expiresAt: row.expires_at,
      startMode: row.schedule_start_mode,
    })),
    sessionsById,
  };
}

export function useAthleteTrainingCalendar({ userId, assignedSessions = [], locale = 'en' }) {
  const [todayISO, setTodayISO] = useState(() => toISODate(new Date()));
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState(todayISO);
  const [completedRows, setCompletedRows] = useState([]);
  const [programSchedule, setProgramSchedule] = useState({ activations: [], sessionsById: {} });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(
      () => setTodayISO(toISODate(new Date())),
      msUntilNextMidnight(new Date()) + 1000
    );
    return () => clearTimeout(timer);
  }, [todayISO]);

  const range = useMemo(() => monthRange(visibleMonth), [visibleMonth]);

  useEffect(() => {
    if (!userId) return undefined;

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const { start, end } = monthWindow(range.from, range.to);
        const [completedRes, schedule] = await Promise.all([
          supabase
            .from('user_training_sessions')
            .select(
              'id, session_id, ended_at, duration_seconds, activity_type, custom_activity_name, sessions(name, session_type)'
            )
            .eq('user_id', userId)
            .eq('status', 'completed')
            .gte('ended_at', start)
            .lte('ended_at', end),
          fetchProgramSchedule(userId),
        ]);

        if (completedRes.error) throw completedRes.error;
        if (!cancelled) {
          setCompletedRows((completedRes.data ?? []).map(mapCompletedRow).filter((row) => row.date));
          setProgramSchedule(schedule);
        }
      } catch (err) {
        console.error('Error fetching calendar data:', err);
        if (!cancelled) {
          setCompletedRows([]);
          setProgramSchedule({ activations: [], sessionsById: {} });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [userId, range.from, range.to]);

  const completedByDate = useMemo(
    () => bucketByDate(completedRows, (row) => row.date),
    [completedRows]
  );

  const activeAssigned = useMemo(
    () => (assignedSessions ?? []).filter((session) => session.is_active !== false),
    [assignedSessions]
  );

  const assignedByDate = useMemo(
    () =>
      bucketByDate(
        activeAssigned.filter((session) => session.due_date),
        (session) => dateKey(session.due_date)
      ),
    [activeAssigned]
  );

  const undatedSessions = useMemo(
    () => activeAssigned.filter((session) => !session.due_date),
    [activeAssigned]
  );

  const programSessions = useMemo(() => {
    return Object.values(programSchedule.sessionsById).reduce((acc, session) => {
      acc[session.programId] = [...(acc[session.programId] ?? []), session];
      return acc;
    }, {});
  }, [programSchedule]);

  const programByDate = useMemo(() => {
    const projections = buildProgramScheduleMap(
      programSchedule.activations,
      programSessions,
      range
    );
    const byDate = new Map();

    projections.forEach((entries, date) => {
      const assignedIds = new Set(
        (assignedByDate.get(date) ?? []).map((session) => session.session_id)
      );
      const sessions = entries
        .filter((entry) => !assignedIds.has(entry.sessionId))
        .map((entry) => programSchedule.sessionsById[entry.sessionId])
        .filter(Boolean);

      if (sessions.length > 0) byDate.set(date, sessions);
    });

    return byDate;
  }, [programSchedule, programSessions, range, assignedByDate]);

  const unstartedPrograms = useMemo(() => {
    return programSchedule.activations
      .filter((activation) => {
        const sessions = programSessions[activation.programId] ?? [];
        return sessions.length > 0 && !hasFutureSessions(activation, sessions, todayISO);
      })
      .map((activation) => ({
        programId: activation.programId,
        name: programSessions[activation.programId]?.[0]?.programName ?? '',
      }));
  }, [programSchedule, programSessions, todayISO]);

  const upcomingPrograms = useMemo(() => {
    return programSchedule.activations
      .map((activation) => {
        const sessions = programSessions[activation.programId] ?? [];
        return {
          programId: activation.programId,
          name: sessions[0]?.programName ?? '',
          startDate: firstProjectedDate(activation, sessions),
        };
      })
      .filter((program) => program.startDate && program.startDate > selectedDate)
      .map((program) => ({
        ...program,
        startLabel: formatStartDateLabel(program.startDate, locale),
      }));
  }, [programSchedule, programSessions, selectedDate, locale]);

  const days = useMemo(
    () =>
      buildMonthCells({
        month: visibleMonth,
        todayISO,
        selectedDate,
        completedByDate,
        assignedByDate,
        programByDate,
      }),
    [visibleMonth, todayISO, selectedDate, completedByDate, assignedByDate, programByDate]
  );

  const monthLabel = useMemo(
    () => visibleMonth.toLocaleDateString(locale, { month: 'long', year: 'numeric' }),
    [visibleMonth, locale]
  );

  const weekdayLabels = useMemo(() => buildWeekdayLabels(locale), [locale]);

  const goToMonth = useCallback(
    (offset) => {
      const next = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + offset, 1);
      setVisibleMonth(next);
      setSelectedDate(defaultDateForMonth(next, todayISO));
    },
    [visibleMonth, todayISO]
  );

  const goToPreviousMonth = useCallback(() => goToMonth(-1), [goToMonth]);
  const goToNextMonth = useCallback(() => goToMonth(1), [goToMonth]);

  return {
    days,
    monthLabel,
    weekdayLabels,
    selectedDate,
    selectedCompleted: completedByDate.get(selectedDate) ?? [],
    selectedAssigned: assignedByDate.get(selectedDate) ?? [],
    selectedProgramSessions: programByDate.get(selectedDate) ?? [],
    undatedSessions,
    upcomingPrograms,
    unstartedPrograms,
    isLoading,
    goToPreviousMonth,
    goToNextMonth,
    selectDate: setSelectedDate,
  };
}
